import { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudSun,
  faCompass,
  faDroplet,
  faTemperatureHalf,
  faWind,
  faTimes,
  faTriangleExclamation,
  faSpinner,
} from '@fortawesome/pro-regular-svg-icons';

import {
  useAppSelector,
  preferencesSelector,
} from '../../store';

import { fetchOpenMeteoWeather } from './utils';
import { CurrentWeather, HourlyForecast, ParsedCoords } from './types';
import {
  formatCoords,
  formatHourLabel,
  formatUpdatedTime,
  formatWindDirection,
  metricIconClass,
  parseCoords,
  resolveWeatherUnitSystem,
  saveWeatherUnitSystem,
  toCurrentWeather,
  toHourlyForecast,
  weatherDescription,
  weatherIcon,
  weatherToneClass,
  WeatherUnitSystem,
} from './utils';

import './WeatherSidebar.scss';

interface WeatherSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WeatherSidebar({ isOpen, onClose }: WeatherSidebarProps) {
  const preferences = useAppSelector(preferencesSelector);
  const savedCoords = preferences.weatherCoords ?? '';
  const hasSavedCoords = savedCoords.trim().length > 0;
  const [unitSystem, setUnitSystem] = useState<WeatherUnitSystem>(() => resolveWeatherUnitSystem());
  const temperatureUnitLabel = unitSystem === 'us' ? '°F' : '°C';
  const windUnitLabel = unitSystem === 'us' ? 'mph' : 'km/h';
  const precipitationUnitLabel = unitSystem === 'us' ? 'in' : 'mm';

  const [weather, setWeather] = useState<CurrentWeather | null>(null);
  const [hourlyForecast, setHourlyForecast] = useState<HourlyForecast[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedCoords, setLastFetchedCoords] = useState<string | null>(null);

  const parsedCoords = useMemo(() => parseCoords(savedCoords), [savedCoords]);
  const coordsError = hasSavedCoords && !parsedCoords
    ? 'Weather coordinates in admin settings are invalid. Use latitude and longitude like 53.3228, -6.2254.'
    : null;

  const handleUnitSystemChange = useCallback((nextUnitSystem: WeatherUnitSystem) => {
    if (nextUnitSystem === unitSystem) return;
    setUnitSystem(nextUnitSystem);
    saveWeatherUnitSystem(nextUnitSystem);
  }, [unitSystem]);

  const fetchWeather = useCallback(async (coords: ParsedCoords) => {
    setError(null);

    try {
      const data = await fetchOpenMeteoWeather(coords, unitSystem);
      const current = toCurrentWeather(data);
      if (!current) {
        throw new Error('Open-Meteo response did not include current weather data');
      }

      setWeather(current);
      setHourlyForecast(toHourlyForecast(data));
      setLastFetchedCoords(formatCoords(coords));
    } catch (fetchError) {
      setWeather(null);
      setHourlyForecast([]);
      setLastFetchedCoords(null);
      setError(fetchError instanceof Error ? fetchError.message : 'Unable to load weather');
    }
  }, [unitSystem]);

  useEffect(() => {
    if (!isOpen || !parsedCoords) return;
    void fetchWeather(parsedCoords);
  }, [fetchWeather, isOpen, parsedCoords]);

  if (!isOpen) return null;

  const statusDescription = weather
    ? weatherDescription(weather.weatherCode, weather.isDay)
    : 'No weather loaded yet';

  return (
    <aside className="weather-sidebar">
      <header className="weather-sidebar__header">
        <div className="weather-sidebar__title">
          <FontAwesomeIcon icon={faCloudSun} />
          <div>
            <h3>Weather</h3>
            <p>Open-Meteo current conditions</p>
          </div>
        </div>
        <button
          className="weather-sidebar__close"
          onClick={onClose}
          aria-label="Close weather panel"
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </header>

      <div className="weather-sidebar__content">
        {error && (
          <section className="weather-sidebar__panel weather-sidebar__panel--input">
            <div className="weather-sidebar__error" role="alert">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>{error}</span>
            </div>
          </section>
        )}

        <section className={cx('weather-sidebar__panel', 'weather-sidebar__panel--summary', { 'weather-sidebar__panel--empty': !weather })}>
          <div className="weather-sidebar__summary-header">
            <div>
              <span className="weather-sidebar__eyebrow">Current weather</span>
              <h4>{statusDescription}</h4>
            </div>
            <span className="weather-sidebar__pill">
              {weather ? `${weather.temperature ?? '—'}${temperatureUnitLabel}` : '—'}
            </span>
          </div>

          <div className="weather-sidebar__unit-switch" role="group" aria-label="Weather units">
            <button
              type="button"
              className={cx('weather-sidebar__unit-button', { 'weather-sidebar__unit-button--active': unitSystem === 'metric' })}
              onClick={() => handleUnitSystemChange('metric')}
              aria-pressed={unitSystem === 'metric'}
            >
              Metric (°C)
            </button>
            <button
              type="button"
              className={cx('weather-sidebar__unit-button', { 'weather-sidebar__unit-button--active': unitSystem === 'us' })}
              onClick={() => handleUnitSystemChange('us')}
              aria-pressed={unitSystem === 'us'}
            >
              US (°F)
            </button>
          </div>

          {coordsError && (
            <div className="weather-sidebar__error" role="alert">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>{coordsError}</span>
            </div>
          )}

          {!parsedCoords && !coordsError && (
            <p className="weather-sidebar__empty-state">
              Set weather coordinates in the admin Theme Editor to show current conditions here.
            </p>
          )}

          {weather ? (
            <>
              <div className="weather-sidebar__reading-grid">
                <article>
                  <FontAwesomeIcon icon={faTemperatureHalf} className={cx('weather-sidebar__metric-icon', metricIconClass('temperature'))} />
                  <span>Temperature</span>
                  <strong>{weather.temperature ?? '—'}{temperatureUnitLabel}</strong>
                </article>
                <article>
                  <FontAwesomeIcon icon={faCloudSun} className={cx('weather-sidebar__metric-icon', metricIconClass('feelsLike'))} />
                  <span>Feels like</span>
                  <strong>{weather.apparentTemperature ?? '—'}{temperatureUnitLabel}</strong>
                </article>
                <article>
                  <FontAwesomeIcon icon={faDroplet} className={cx('weather-sidebar__metric-icon', metricIconClass('humidity'))} />
                  <span>Humidity</span>
                  <strong>{weather.humidity ?? '—'}%</strong>
                </article>
                <article>
                  <FontAwesomeIcon icon={faWind} className={cx('weather-sidebar__metric-icon', metricIconClass('wind'))} />
                  <span>Wind</span>
                  <strong>{weather.windSpeed ?? '—'} {windUnitLabel}</strong>
                </article>
                <article>
                  <FontAwesomeIcon icon={faCompass} className={cx('weather-sidebar__metric-icon', metricIconClass('direction'))} />
                  <span>Wind direction</span>
                  <strong>{formatWindDirection(weather.windDirection)}</strong>
                </article>
                <article>
                  <FontAwesomeIcon icon={faDroplet} className={cx('weather-sidebar__metric-icon', metricIconClass('precipitation'))} />
                  <span>Precipitation</span>
                  <strong>{weather.precipitation ?? '—'} {precipitationUnitLabel}</strong>
                </article>
              </div>

              <section className="weather-sidebar__hourly">
                <div className="weather-sidebar__hourly-header">
                  <span>Next hours</span>
                  <span>8-hour strip</span>
                </div>
                <div className="weather-sidebar__hourly-strip" aria-label="Hourly weather forecast">
                  {hourlyForecast.length > 0 ? hourlyForecast.map((hour) => (
                    <article key={hour.time} className="weather-sidebar__hourly-card">
                      <FontAwesomeIcon
                        className={cx(
                          'weather-sidebar__hourly-weather-icon',
                          `weather-sidebar__hourly-weather-icon--${weatherToneClass(hour.weatherCode, weather.isDay)}`
                        )}
                        icon={weatherIcon(hour.weatherCode, weather.isDay)}
                      />
                      <span className="weather-sidebar__hourly-time">{formatHourLabel(hour.time)}</span>
                      <strong>{hour.temperature ?? '—'}°</strong>
                      <span className="weather-sidebar__hourly-icon">
                        {weatherDescription(hour.weatherCode, weather.isDay)}
                      </span>
                      <span className="weather-sidebar__hourly-meta">
                        {hour.precipitationProbability ?? '—'}% rain
                      </span>
                      <span className="weather-sidebar__hourly-meta">
                        {hour.windSpeed ?? '—'} {windUnitLabel} wind
                      </span>
                    </article>
                  )) : (
                    <p className="weather-sidebar__hourly-empty">Hourly forecast is unavailable for this location.</p>
                  )}
                </div>
              </section>

              <div className="weather-sidebar__footer-meta">
                <span>{lastFetchedCoords}</span>
                <span>{formatUpdatedTime(weather.time)}</span>
              </div>
            </>
          ) : (
            <p className="weather-sidebar__empty-state">
              <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
              &nbsp;
              Waiting for weather data...
            </p>
          )}
        </section>
      </div>
    </aside>
  );
}

export default WeatherSidebar;
